// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TitleTypography, {
    TitleTypographyProps,
    TitleTypographyStyles,
} from "@flip/App/Components/Common/TitleTypography";
import AngleDown from "@flip/XDL/Icons/AngleDown";
import AngleUp from "@flip/XDL/Icons/AngleUp";
import { color, mergeWithCss, xScreen } from "@flip/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Accordion from "@insite/mobius/Accordion";
import {
    AccordionSectionPanel,
    AccordionSectionPresentationProps,
    ManagedAccordionSection,
} from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    title?: React.ReactNode;
    isAccordion?: boolean;
    className?: string;
    extendedStyles?: SectionWrapperStyles;
    withBorder?: boolean;
}

type Props = OwnProps & Omit<TitleTypographyProps, "extendedStyles">;

export interface SectionaccordionIconStyles {
    managerAccordionSection?: AccordionSectionPresentationProps;
    bodyGridContainer?: GridContainerProps;
}

export interface SectionWrapperStyles {
    title?: TitleTypographyStyles;
    accrodionTitle?: TitleTypographyStyles;
    wrapper?: InjectableCss;
    accordionStyles?: SectionaccordionIconStyles;
}

const sectionWrapperStyles: SectionWrapperStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            /* Start XNGAGE Changes */
            @media print {
                display: block;
            }
            /* END XNGAGE Changes */

            ${({ withBorder }: any) => {
                if (withBorder) {
                    return css`
                        border-bottom: 1px solid ${color("common.border")};
                        padding-bottom: 32px;
                        margin-bottom: 32px;
                    `;
                }
            }}
        `,
    },
    accordionStyles: {
        managerAccordionSection: {
            mergeCss: true,
            toggleIconProps: {
                mergeCss: true,
                src: AngleDown,
                size: 25,
            },
            collapseIconProps: {
                mergeCss: true,
                size: 25,
                src: AngleUp,
            },
            headerProps: {
                css: css`
                    background: transparent;
                    padding-bottom: 10px;
                    border-top: 0;
                    border-bottom: 1px solid ${color("common.border")};
                    button {
                        display: flex;
                        align-items: center;
                        box-shadow: none;
                        background: transparent;
                        &,
                        &:focus {
                            border: 0;
                            padding: 0 16px 0 0;
                        }
                    }
                `,
            },
            panelProps: {
                css: css`
                    background: transparent;
                    padding: 0;
                    border: 0;
                `,
            },
        },
        bodyGridContainer: {
            gap: 0,
            css: css`
                margin-top: 16px;
            `,
        },
    },
    accrodionTitle: {
        wrapper: {
            css: css`
                margin-bottom: 0;
                align-items: baseline;
            `,
        },
        title: {
            variant: "h3",
        },
    },
};

const SectionWrapper: FC<Props> = ({
    extendedStyles,
    children,
    link,
    onLinkClick,
    linkText,
    openInNewTab,
    title,
    isAccordion,
    className,
    extraChild,
    withBorder,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(sectionWrapperStyles, extendedStyles));

    const titleTypograhy = title ? (
        <TitleTypography
            link={link}
            onLinkClick={onLinkClick}
            linkText={linkText}
            openInNewTab={openInNewTab}
            extendedStyles={isAccordion ? styles.accrodionTitle : styles.title}
            extraChild={extraChild}
        >
            {title}
        </TitleTypography>
    ) : null;

    if (isAccordion) {
        return (
            <StyledWrapper className={`accordion-section-wrapper ${className || ""}`}>
                <Accordion headingLevel={2}>
                    <ManagedAccordionSection
                        title={titleTypograhy}
                        initialExpanded={true}
                        {...styles.accordionStyles?.managerAccordionSection}
                    >
                        <GridContainer {...styles.accordionStyles?.bodyGridContainer}>{children}</GridContainer>
                    </ManagedAccordionSection>
                </Accordion>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper
            className={`section-wrapper ${className || ""}`}
            {...styles.wrapper}
            {...{ withBorder }}
            {...otherProps}
        >
            {titleTypograhy}
            <StyledWrapper>{children}</StyledWrapper>
        </StyledWrapper>
    );
};

export default SectionWrapper;
